//
// Copyright 2026 DXOS.org
//

import * as semver from 'semver';

import { EncodedReference, type QueryAST, isEncodedReference } from '@dxos/echo-protocol';
import { DXN, EID, EntityId } from '@dxos/keys';
import { assumeType } from '@dxos/util';

import { getTypeURI } from '../Annotation/annotations.ts';
import { getMetaChecked } from '../common/api/meta.ts';
import { type AnyEntity, ParentId } from '../common/types/index.ts';
import { type InternalObjectProps } from '../Entity/model.ts';
import { objectToJSON } from '../Obj/json-serializer.ts';

/**
 * Matches a tag filter against an object's stored tags. Tags may be stored as encoded references or
 * (in legacy data) bare URI strings, and those URIs may be in legacy DXN form. Both sides are
 * normalized to the canonical EID before comparing.
 */
export const matchesTag = (tags: readonly unknown[], filterTag: string): boolean => {
  const canonical = (uri: string): string => {
    const eid = EID.tryParse(uri);
    return (eid && EID.getEntityId(eid)) || uri;
  };
  const normalize = (tag: unknown): string =>
    canonical(isEncodedReference(tag) ? EncodedReference.toURI(tag) : (tag as string));
  const target = canonical(filterTag);
  return tags.some((tag) => normalize(tag) === target);
};

/**
 * Matches a meta `key` / `version` constraint against an object's meta `key` and `version`.
 */
export const matchMetaKey = (
  key: string,
  versionRange: string | undefined,
  objKey: string | undefined,
  objVersion: string | undefined,
): boolean => {
  if (objKey !== key) {
    return false;
  }
  if (versionRange === undefined) {
    return true;
  }
  if (objVersion === undefined || semver.valid(objVersion) === null) {
    return false;
  }
  if (semver.validRange(versionRange) === null) {
    return false;
  }
  return semver.satisfies(objVersion, versionRange, { includePrerelease: true });
};

/**
 * Compares a filter's type discriminator against the value stored on an object's `system.type`.
 */
export const compareTypenameStrings = (expectedStr: string, actualStr: string): boolean => {
  // Identical strings agree under every branch below, and a type query compares the same pair once
  // per candidate, so the parse is skipped rather than repeated.
  if (expectedStr === actualStr) {
    return true;
  }

  const expectedEid = EID.tryParse(expectedStr);
  if (expectedEid) {
    const actualEid = EID.tryParse(actualStr);
    if (!actualEid) {
      return false;
    }
    if (EID.getEntityId(expectedEid) !== EID.getEntityId(actualEid)) {
      return false;
    }
    const expectedSpaceId = EID.getSpaceId(expectedEid);
    const actualSpaceId = EID.getSpaceId(actualEid);
    return expectedSpaceId === undefined || actualSpaceId === undefined || expectedSpaceId === actualSpaceId;
  }

  const expectedDxn = DXN.tryMake(expectedStr);
  if (expectedDxn) {
    const actualDxn = DXN.tryMake(actualStr);
    if (!actualDxn) {
      return false;
    }
    if (DXN.getName(expectedDxn) !== DXN.getName(actualDxn)) {
      return false;
    }
    const expectedVersion = DXN.getVersion(expectedDxn);
    const actualVersion = DXN.getVersion(actualDxn);
    return expectedVersion === undefined || actualVersion === undefined || expectedVersion === actualVersion;
  }

  return expectedStr === actualStr;
};

const structuralMatch = (filterObj: any, targetObj: any, strict = true): boolean => {
  if (typeof filterObj !== 'object' || filterObj === null) {
    return filterObj === targetObj;
  }

  if (typeof targetObj !== 'object' || targetObj === null) {
    return false;
  }

  const filterKeys = Object.keys(filterObj);
  const targetKeys = Object.keys(targetObj);
  if (strict && filterKeys.length !== targetKeys.length) {
    return false;
  }

  return filterKeys.every((key) => {
    if (!(key in targetObj)) {
      return false;
    }
    const filterValue = filterObj[key];
    const targetValue = targetObj[key];

    if (typeof filterValue === 'object' && filterValue !== null) {
      return structuralMatch(filterValue, targetValue);
    }

    return filterValue === targetValue;
  });
};

/** Normalizes an encoded reference to its URI string so ref-valued properties compare by identity. */
const _normalizeInValue = (value: unknown): unknown =>
  isEncodedReference(value) ? EncodedReference.toURI(value) : value;

/**
 * Memoizes a `Set` per `values` array so `Filter.in` membership is O(1) rather than
 * `Array.includes`'s O(n) — this matters most for large sets projected from a subquery
 * (`Filter.in(query.project(...))`, resolved to a literal `in` by the query executor), but
 * also speeds up ordinary literal-list `Filter.in`. Keyed by array identity: the planner and
 * executor never mutate a filter's `values` array in place, so the cache stays valid for the
 * array's lifetime.
 */
const _inSetCache = new WeakMap<readonly unknown[], Set<unknown>>();
const _getInSet = (values: readonly unknown[]): Set<unknown> => {
  let set = _inSetCache.get(values);
  if (!set) {
    set = new Set(values.map(_normalizeInValue));
    _inSetCache.set(values, set);
  }
  return set;
};

export const filterMatchValue = (filter: QueryAST.Filter, value: unknown): boolean => {
  switch (filter.type) {
    case 'compare': {
      const compareValue = filter.value as any;
      switch (filter.operator) {
        case 'eq':
          if (isEncodedReference(compareValue)) {
            if (!isEncodedReference(value)) {
              return false;
            }
            return EncodedReference.toURI(value) === EncodedReference.toURI(compareValue);
          }
          return value === compareValue;
        case 'neq':
          return value !== compareValue;
        case 'gt':
          return (value as any) > compareValue;
        case 'gte':
          return (value as any) >= compareValue;
        case 'lt':
          return (value as any) < compareValue;
        case 'lte':
          return (value as any) <= compareValue;
        default:
          return false;
      }
    }
    case 'object': {
      if (typeof value !== 'object' || value === null) {
        return false;
      }

      if (filter.props) {
        for (const [key, valueFilter] of Object.entries(filter.props)) {
          const nestedValue = (value as any)[key];
          if (!filterMatchValue(valueFilter, nestedValue)) {
            return false;
          }
        }
      }

      return true;
    }
    case 'in': {
      return _getInSet(filter.values).has(_normalizeInValue(value));
    }
    case 'in-query': {
      throw new Error('in-query filters must be resolved to a literal `in` by the query executor before matching.');
    }
    case 'contains': {
      if (!Array.isArray(value)) {
        return false;
      }

      return value.some((element) => {
        if (typeof filter.value === 'object' && filter.value !== null && !Array.isArray(filter.value)) {
          return structuralMatch(filter.value, element);
        }

        return element === filter.value;
      });
    }
    case 'range': {
      return (value as any) >= filter.from && (value as any) <= filter.to;
    }
    case 'not': {
      return !filterMatchValue(filter.filter, value);
    }
    case 'and': {
      return filter.filters.every((f) => filterMatchValue(f, value));
    }
    case 'or': {
      return filter.filters.some((f) => filterMatchValue(f, value));
    }
    default:
      return false;
  }
};

/**
 * Meta as the filter walker needs it, flattened out of whichever representation supplies it.
 */
export type FilterRecordMeta = {
  keys?: readonly { source?: string; id?: string }[];
  key?: string;
  version?: string;
  /** Encoded references or bare URI strings — {@link matchesTag} normalizes both. */
  tags?: readonly unknown[];
  annotations?: Readonly<Record<string, unknown>>;
};

/**
 * Reads the fields a filter needs out of one representation of an entity.
 *
 * The representations an executor matches against — a live proxy, a raw Automerge document, the
 * `ObjectJSON` form — walk the filter AST identically and differ only in where each field lives, so
 * one walker over an accessor replaces a copy of the walk per representation.
 */
export interface FilterRecordAccessor<T> {
  getId(record: T): string;
  getTypeURI(record: T): string | undefined;

  /** Filterable data properties by name, or `undefined` when the record cannot be read at all. */
  getProps(record: T): Record<string, unknown> | undefined;
  getMeta(record: T): FilterRecordMeta;
  hasParent(record: T): boolean;

  /**
   * In-memory full-text match. Only representations backed by no index implement it; the rest
   * return `false` and leave text search to the index.
   */
  matchTextSearch(filter: QueryAST.Filter & { type: 'text-search' }, record: T): boolean;
}

/**
 * Builds a filter matcher over one record representation.
 * The leaf predicates ({@link filterMatchValue}, {@link compareTypenameStrings}, {@link matchesTag},
 * {@link matchMetaKey}) are shared regardless — this shares the tree walk around them.
 */
export const makeFilterMatcher = <T>(
  accessor: FilterRecordAccessor<T>,
): ((filter: QueryAST.Filter, record: T) => boolean) => {
  const match = (filter: QueryAST.Filter, record: T): boolean => {
    switch (filter.type) {
      case 'object': {
        if (filter.typename !== null) {
          const typeURI = accessor.getTypeURI(record);
          if (!typeURI || !compareTypenameStrings(filter.typename, typeURI)) {
            return false;
          }
        }

        if (filter.id && filter.id.length > 0 && !filter.id.includes(accessor.getId(record))) {
          return false;
        }

        if (filter.props) {
          const props = accessor.getProps(record);
          if (props === undefined) {
            return false;
          }
          for (const [key, valueFilter] of Object.entries(filter.props)) {
            // `@`-prefixed keys address annotations (`@type`, `@meta`), never data.
            if (key.startsWith('@')) {
              continue;
            }
            if (!filterMatchValue(valueFilter, props[key])) {
              return false;
            }
          }
        }

        // Meta is read lazily: on a live proxy it costs a sub-proxy walk that most filters never need.
        const foreignKeys =
          filter.foreignKeys != null && filter.foreignKeys.length > 0 ? filter.foreignKeys : undefined;
        if (foreignKeys !== undefined || filter.metaKey !== undefined) {
          const meta = accessor.getMeta(record);
          if (
            foreignKeys !== undefined &&
            !foreignKeys.some((fk) => meta.keys?.some((key) => key.source === fk.source && key.id === fk.id))
          ) {
            return false;
          }
          if (
            filter.metaKey !== undefined &&
            !matchMetaKey(filter.metaKey, filter.metaVersion, meta.key, meta.version)
          ) {
            return false;
          }
        }

        return true;
      }

      case 'tag': {
        return matchesTag(accessor.getMeta(record).tags ?? [], filter.tag);
      }

      case 'annotation': {
        const annotations = accessor.getMeta(record).annotations;
        if (annotations === undefined || !(filter.key in annotations)) {
          return false;
        }
        return filter.value === undefined || annotations[filter.key] === filter.value;
      }

      case 'text-search': {
        return accessor.matchTextSearch(filter, record);
      }

      case 'timestamp': {
        throw new Error('Timestamp filters must be handled at the index level, not in-memory matching.');
      }

      case 'child-of': {
        throw new Error('child-of filters must be handled at the executor level, not in-memory matching.');
      }

      case 'changes': {
        throw new Error('changes filters select change records on the host; they never match an object.');
      }

      case 'has-parent': {
        return accessor.hasParent(record) === filter.value;
      }

      case 'mnemonic': {
        return EntityId.getMnemonic(accessor.getId(record)) === filter.mnemonic;
      }

      case 'in-query': {
        throw new Error('in-query filters must be resolved to a literal `in` by the query executor before matching.');
      }

      case 'not': {
        return !match(filter.filter, record);
      }

      case 'and': {
        return filter.filters.every((f) => match(f, record));
      }

      case 'or': {
        return filter.filters.some((f) => match(f, record));
      }

      default:
        return false;
    }
  };

  return match;
};

const entityAccessor: FilterRecordAccessor<AnyEntity> = {
  getId: (entity) => entity.id,
  getTypeURI: (entity) => getTypeURI(entity),
  getProps: (entity) => {
    try {
      return objectToJSON(entity);
    } catch {
      // Serialization of complex static-type entities may throw.
      return undefined;
    }
  },
  getMeta: (entity) => {
    const meta = getMetaChecked(entity);
    return {
      keys: meta.keys,
      key: meta.key,
      version: meta.version,
      annotations: meta.annotations,
      // Lazy: meta tags surface as entities on a live proxy and only the `tag` arm needs their ids.
      get tags() {
        return meta.tags.map((tag) => tag.id);
      },
    };
  },
  hasParent: (entity) => {
    assumeType<InternalObjectProps>(entity);
    return entity[ParentId] !== undefined;
  },
  matchTextSearch: (filter, entity) => matchesTextSearch(filter, entity),
};

/**
 * Matches a filter against an entity proxy without full JSON serialization when possible.
 *
 * Text filters evaluate in memory here because this matcher serves executors with no index behind
 * them (the registry, `Filter.toPredicate`); index-backed paths use `filterMatchDoc` instead.
 */
export const filterMatchEntity: (filter: QueryAST.Filter, entity: AnyEntity) => boolean =
  makeFilterMatcher(entityAccessor);

/**
 * In-memory full-text match: every term must appear (case-insensitive) in the entity's serialized
 * strings, meta included so a registry key is searchable. Containment, not tokenized ranking.
 */
const matchesTextSearch = (filter: QueryAST.Filter & { type: 'text-search' }, entity: AnyEntity): boolean => {
  // A vector query is meaningless without an embedding index, whatever the executor.
  if (filter.searchKind === 'vector') {
    return false;
  }
  const terms = filter.text
    .toLowerCase()
    .split(/\s+/)
    .filter((term) => term.length > 0);
  if (terms.length === 0) {
    return true;
  }
  let json: unknown;
  try {
    json = objectToJSON(entity);
  } catch {
    return false;
  }
  const haystack: string[] = [];
  collectStrings(json, haystack, new Set());
  const text = haystack.join('\n').toLowerCase();
  return terms.every((term) => text.includes(term));
};

const collectStrings = (value: unknown, out: string[], seen: Set<unknown>): void => {
  if (typeof value === 'string') {
    out.push(value);
    return;
  }
  if (value === null || typeof value !== 'object' || seen.has(value)) {
    return;
  }
  seen.add(value);
  for (const entry of Array.isArray(value) ? value : Object.values(value)) {
    collectStrings(entry, out, seen);
  }
};
